import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EmailIcon from "@mui/icons-material/Email";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import VerifiedIcon from "@mui/icons-material/Verified";
import BusinessIcon from "@mui/icons-material/Business";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { SupplierDirectoryItem } from "../../../../types/suppliers";
import styles from "./supplierDetail.module.scss";

interface SupplierDetailProps {
  supplier: SupplierDirectoryItem;
  onBack: () => void;
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(2)}`;
};

const getDdStatusConfig = (status: string) => {
  const statusLower = status.toLowerCase();
  const statusMap: Record<
    string,
    { label: string; color: string; bgColor: string }
  > = {
    active: { label: status, color: "#08ADC2", bgColor: "#e8f5e9" },
    inactive: { label: status, color: "#f44336", bgColor: "#ffebee" },
    onboarding: { label: status, color: "#2196f3", bgColor: "#e3f2fd" },
    evaluation: { label: status, color: "#ff9800", bgColor: "#fff3e0" },
    draft: { label: status, color: "#9e9e9e", bgColor: "#f5f5f5" },
    approved: { label: status, color: "#4caf50", bgColor: "#e8f5e9" },
    information_requested: {
      label: status,
      color: "#ff9800",
      bgColor: "#fff3e0",
    },
    pending: { label: status, color: "#9c27b0", bgColor: "#f3e5f5" },
  };

  for (const [key, value] of Object.entries(statusMap)) {
    if (statusLower.includes(key)) {
      return value;
    }
  }

  return { label: status, color: "#666666", bgColor: "#f5f5f5" };
};

const SupplierDetail: React.FC<SupplierDetailProps> = ({
  supplier,
  onBack,
}) => {
  const statusConfig = useMemo(
    () => getDdStatusConfig(supplier.status),
    [supplier.status],
  );

  return (
    <Box className={styles.container}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        className={styles.backButton}
      >
        Back to Suppliers
      </Button>

      {/* Header Section */}
      <Box className={styles.header}>
        <Box className={styles.headerLeft}>
          <Typography variant="h4" className={styles.supplierName}>
            {supplier.supplierName}
          </Typography>
          {/* <Box className={styles.headerMeta}>
            <Box className={styles.metaItem}>
              <LocationOnIcon className={styles.metaIcon} />
              <Typography className={styles.metaText}>
                {supplier.country || "N/A"}
              </Typography>
            </Box>
            <Typography className={styles.metaSeparator}>•</Typography>
            <Typography className={styles.metaText}>
              Updated: {supplier.companyOverview.updatedAt}
            </Typography>
            <Typography className={styles.metaSeparator}>•</Typography>
            <Chip
              label={statusConfig.label}
              className={styles.statusChip}
              style={{
                backgroundColor: statusConfig.bgColor,
                color: statusConfig.color,
              }}
              size="small"
            />
          </Box> */}
        </Box>
        {/* <Box className={styles.headerActions}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            className={styles.actionButton}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            className={styles.contactButton}
          >
            Contact
          </Button>
        </Box> */}
      </Box>

      {/* Metric Cards */}
      <Box className={styles.metricsGrid}>
        <Card className={styles.metricCard}>
          <CardContent className={styles.metricCardContent}>
            <Box
              className={styles.metricIconWrapper}
              style={{ backgroundColor: "#e8f5e9" }}
            >
              <AttachMoneyIcon
                className={styles.metricIcon}
                style={{ color: "#08ADC2" }}
              />
            </Box>
            <Box className={styles.metricInfo}>
              <Typography className={styles.metricLabel}>
                Total Spend
              </Typography>
              <Typography className={styles.metricValue}>
                {formatCurrency(supplier.totalSpend)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card className={styles.metricCard}>
          <CardContent className={styles.metricCardContent}>
            <Box
              className={styles.metricIconWrapper}
              style={{ backgroundColor: "#e3f2fd" }}
            >
              <DescriptionIcon
                className={styles.metricIcon}
                style={{ color: "#5b6b9e" }}
              />
            </Box>
            <Box className={styles.metricInfo}>
              <Typography className={styles.metricLabel}>
                Active Contracts
              </Typography>
              <Typography className={styles.metricValue}>
                {supplier.activeContractsCount}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* <Card className={styles.metricCard}>
          <CardContent className={styles.metricCardContent}>
            <Box
              className={styles.metricIconWrapper}
              style={{ backgroundColor: "#f3e5f5" }}
            >
              <CategoryIcon
                className={styles.metricIcon}
                style={{ color: "#9c27b0" }}
              />
            </Box>
            <Box className={styles.metricInfo}>
              <Typography className={styles.metricLabel}>Category</Typography>
              <Typography className={styles.metricValue}>
                {supplier.category}
              </Typography>
            </Box>
          </CardContent>
        </Card> */}

        <Card className={styles.metricCard}>
          <CardContent className={styles.metricCardContent}>
            <Box
              className={styles.metricIconWrapper}
              style={{ backgroundColor: "#fff3e0" }}
            >
              <VerifiedIcon
                className={styles.metricIcon}
                style={{ color: "#ff9800" }}
              />
            </Box>
            <Box className={styles.metricInfo}>
              <Typography className={styles.metricLabel}>
                Certifications
              </Typography>
              <Typography className={styles.metricValue}>
                {supplier.certificatesCount.length}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Company Overview & Contact Info */}
      <Box className={styles.infoGrid}>
        <Card className={styles.infoCard}>
          <CardContent>
            <Box className={styles.cardHeader}>
              <BusinessIcon className={styles.cardIcon} />
              <Typography className={styles.cardTitle}>
                Company Overview
              </Typography>
            </Box>
            <List className={styles.infoList}>
              <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="Supplier ID"
                  secondary={supplier.supplierId}
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem>
              <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="Category"
                  secondary={supplier.category}
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem>
              <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="Country"
                  secondary={supplier.companyOverview.localOrForeign || "N/A"}
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem>
              <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="Status"
                  secondary={supplier.companyOverview.status}
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem>
              <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="Last Updated"
                  secondary={supplier.companyOverview.updatedAt}
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        <Card className={styles.infoCard}>
          <CardContent>
            <Box className={styles.cardHeader}>
              <ContactMailIcon className={styles.cardIcon} />
              <Typography className={styles.cardTitle}>
                Contact Information
              </Typography>
            </Box>
            <List className={styles.infoList}>
              <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="Location Name"
                  secondary={supplier.contactInformation.locName || "N/A"}
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem>
              <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="Address"
                  secondary={
                    [
                      supplier.contactInformation.locStreet1,
                      supplier.contactInformation.locStreet2,
                      supplier.contactInformation.locStreet3,
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"
                  }
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem>
              <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="City"
                  secondary={supplier.contactInformation.city || "N/A"}
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem>
              {/* <ListItem className={styles.infoListItem}>
                <ListItemText
                  primary="Country"
                  secondary={supplier.contactInformation.country || "N/A"}
                  classes={{
                    primary: styles.infoLabel,
                    secondary: styles.infoValue,
                  }}
                />
              </ListItem> */}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Certifications & Compliance */}
      <Card className={styles.certificationsCard}>
        <CardContent>
          <Typography className={styles.sectionTitle}>
            Certifications & Compliance
          </Typography>
          <Box className={styles.certificationsGrid}>
            {supplier.certificatesCount &&
            supplier.certificatesCount.length > 0 ? (
              supplier.certificatesCount.map((cert, index) => (
                <Box key={index} className={styles.certificationBadge}>
                  <VerifiedIcon className={styles.certBadgeIcon} />
                  <Box className={styles.certBadgeContent}>
                    <Typography className={styles.certBadgeName}>
                      {cert.type.replace(/_/g, " ").toUpperCase()}
                    </Typography>
                    {cert.expiry_date && (
                      <Typography className={styles.certExpiryDate}>
                        Expiry Date: {cert.expiry_date}
                      </Typography>
                    )}
                    {/* <Chip
                      label="Verified"
                      size="small"
                      className={styles.certBadgeStatus}
                      style={{
                        backgroundColor: "#e8f5e9",
                        color: "#08ADC2",
                      }}
                    /> */}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography className={styles.noDataText}>
                No certifications available
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Active Contracts */}
      <Card className={styles.contractsCard}>
        <CardContent>
          <Typography className={styles.sectionTitle}>
            Active Contracts ({supplier.contracts?.length || 0})
          </Typography>
          <List className={styles.contractsList}>
            {supplier.contracts && supplier.contracts.length > 0 ? (
              supplier.contracts.map((contract) => (
                <ListItem
                  key={contract.contractId}
                  className={styles.contractItem}
                >
                  <Box className={styles.contractInfo}>
                    <Typography className={styles.contractNumber}>
                      #{contract.contractId}
                    </Typography>
                    <Typography className={styles.contractTitle}>
                      {contract.contractName}
                    </Typography>
                  </Box>
                  <Box className={styles.contractMeta}>
                    <Typography className={styles.contractValue}>
                      {formatCurrency(contract.maximumValue)}
                    </Typography>
                    <Chip
                      label={contract.contractType}
                      size="small"
                      className={styles.contractStatus}
                      style={{
                        backgroundColor: "#e8f5e9",
                        color: "#08ADC2",
                        textTransform: "capitalize",
                      }}
                    />
                  </Box>
                </ListItem>
              ))
            ) : (
              <Typography className={styles.noDataText}>
                No contracts available
              </Typography>
            )}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SupplierDetail;
